import React from 'react';

import classNames from 'classnames';
import { Tooltip } from '@mui/material';
import { IconButton } from '@infoods/lifebase-ui';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import classes from './LbToggleTabsList.module.scss';
import { TabOption, TabsConfig, TabValue } from '../types/TabsConfig';
import { useTabsContext } from '../context/TabsContext';
import { useScrollableTabs } from './hooks/useScrollableTabs/useScrollableTabs';

export type LbToggleTabsListProps = {
  tabsConfig: TabOption[] | TabsConfig[];
  fullWidth?: boolean;
  className?: string;
  size?: 'small' | 'medium';
};

export const LbToggleTabsList = ({
  tabsConfig,
  fullWidth,
  className,
  size = 'medium',
}: LbToggleTabsListProps) => {
  const { value, onValueChange } = useTabsContext();
  const {
    scrollerRef,
    tabListRef,
    displayStartScroll,
    displayEndScroll,
    handleStartScrollClick,
    handleEndScrollClick,
  } = useScrollableTabs();

  const rootClasses = classNames(className, classes.container, classes.colorWhite, {
    [classes.fullWidth]: fullWidth,
    [classes.small]: size === 'small',
    [classes.medium]: size === 'medium',
  });

  const handleTabClick = (tabValue: TabValue) => {
    onValueChange(tabValue);
  };

  const tabItems = tabsConfig.map(
    (tab) =>
      !tab.hide && (
        <Tooltip
          title={tab.tooltipText}
          placement="top"
          arrow
          slotProps={{ tooltip: { className: classes.tooltip } }}
          key={tab.value}
        >
          <button
            type="button"
            role="tab"
            aria-selected={value === tab.value}
            aria-controls={`panel-${tab.value}`}
            id={`tab-${tab.value}`}
            className={classNames(classes.tab, {
              [classes.selected]: value === tab.value,
            })}
            disabled={tab.disabled}
            onClick={() => handleTabClick(tab.value)}
          >
            {!!tab.icon && <span className={classes.icon}>{tab.icon}</span>}
            {!!tab.label && <span>{tab.label}</span>}
          </button>
        </Tooltip>
      ),
  );

  return (
    <div className={rootClasses}>
      {displayStartScroll && (
        <div
          className={classNames(
            classes.scrollButtonContainer,
            classes.scrollButtonStartContainer,
          )}
        >
          <IconButton onClick={handleStartScrollClick} tabIndex={-1}>
            <ChevronLeftRoundedIcon fontSize="medium" />
          </IconButton>
        </div>
      )}
      <div className={classes.scroller} ref={scrollerRef}>
        <div className={classes.tabRow} role="tablist" ref={tabListRef}>
          {tabItems}
        </div>
      </div>
      {displayEndScroll && (
        <div
          className={classNames(
            classes.scrollButtonContainer,
            classes.scrollButtonEndContainer,
          )}
        >
          <IconButton onClick={handleEndScrollClick} tabIndex={-1}>
            <ChevronRightRoundedIcon fontSize="medium" />
          </IconButton>
        </div>
      )}
    </div>
  );
};
